import cn from 'classnames'
import React, { useEffect, useRef, useState } from 'react'

import { PremiumFollowSolution } from './PremiumFollowSolution'

interface PremiumFollowActionButtonProps {
  className?: string
  followTypeName?: string | null
  note?: string | null
  onShowAddNote?: (show: boolean) => void
  handleFollow?: (type: 'Portfolio' | 'Reviewed' | 'Watching', updateType: 'Move' | 'AddNote' | 'Remove') => void
  handleNoteAction?: (note: string, updateType: 'AddNote' | 'RemoveNote') => void
  setEditMode?: (editMode: boolean) => void
  isFrom?: string
  sharedPortfolioId?: string | null
  solutionId?: string | null
  onShowAddCustomTag?: () => void
  /** When false, the "Add Custom Tag" action is hidden (e.g. search vs my-followings). */
  showAddCustomTag?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const PremiumFollowActionButton: React.FC<PremiumFollowActionButtonProps> = ({
  className,
  followTypeName,
  note = null,
  onShowAddNote = () => {},
  handleFollow = () => {},
  handleNoteAction = () => {},
  setEditMode = () => {},
  sharedPortfolioId = null,
  solutionId = null,
  onShowAddCustomTag = () => {},
  showAddCustomTag = false,
}) => {
  const [showActions, setShowActions] = useState(false)
  const actionsRef = useRef<HTMLDivElement>(null)
  const [showFollowType, setShowFollowType] = useState(false)

  const toggleShowActions = () => {
    setShowActions((prev) => !prev)
  }

  const handleCloseShowFollowType = () => {
    setShowFollowType(false)
  }

  const handleFollowType = async (type: 'Portfolio' | 'Reviewed' | 'Watching') => {
    setEditMode(false)
    setShowFollowType(false)
    handleFollow?.(type, 'Move')
  }
  const openFollowType = () => {
    setShowActions(false)
    setShowFollowType(true)
  }

  // ✅ Close actions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowActions(false)
      }
    }

    if (showActions) {
      document.addEventListener('click', handleClickOutside)
    } else {
      document.removeEventListener('click', handleClickOutside)
    }

    // Cleanup listener when component unmounts or showActions changes
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showActions])

  const openAddNote = () => {
    setShowActions(false)
    onShowAddNote?.(true)
  }

  const openAddCustomTag = () => {
    setShowActions(false)
    onShowAddCustomTag?.()
  }

  const handleRemove = () => {
    setEditMode(false)
    setShowActions(false)
    handleFollow?.('Portfolio', 'Remove')
  }

  const handleDeleteNote = () => {
    setEditMode(false)
    setShowActions(false)
    handleNoteAction?.(note || '', 'RemoveNote')
  }

  return (
    <div className={cn('relative flex', className)} ref={actionsRef}>
      <button
        className="w-[28px] h-[28px] opacity-100 rounded-[4px] border border-[#dddddd]"
        onClick={toggleShowActions}
      >
        <svg width="26" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 10C4.9 10 4 10.9 4 12C4 13.1 4.9 14 6 14C7.1 14 8 13.1 8 12C8 10.9 7.1 10 6 10ZM18 10C16.9 10 16 10.9 16 12C16 13.1 16.9 14 18 14C19.1 14 20 13.1 20 12C20 10.9 19.1 10 18 10ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10Z"
            fill="#333333"
          />
        </svg>
      </button>

      {showActions && (
        <div className="absolute top-[30px] right-[0px] z-10 w-[150px] rounded-[8px] bg-white opacity-100 shadow-[0_1px_3px_0_rgba(32,42,53,0.2)]">
          <div className="flex flex-col pb-[1px]">
            <div
              className="flex h-[40px] cursor-pointer flex-row px-[12px] font-poppins text-[14px] font-medium leading-[40px] text-[#011D58] hover:bg-[#F5F5F5]"
              onClick={openFollowType}
            >
              Move
            </div>
            <div
              className="flex h-[40px] cursor-pointer flex-row px-[12px] font-poppins text-[14px] font-medium leading-[40px] text-[#011D58] hover:bg-[#F5F5F5]"
              onClick={openAddNote}
            >
              {note ? 'Edit Note' : 'Add Note'}
            </div>
            {note && (
              <div
                className="flex h-[40px] cursor-pointer flex-row px-[12px] font-poppins text-[14px] font-medium leading-[40px] text-[#011D58] hover:bg-[#F5F5F5]"
                onClick={handleDeleteNote}
              >
                Delete Note
              </div>
            )}
            {showAddCustomTag && (
              <div
                className="flex h-[40px] cursor-pointer flex-row px-[12px] font-poppins text-[14px] font-medium leading-[40px] text-[#011D58] hover:bg-[#F5F5F5]"
                onClick={openAddCustomTag}
              >
                Add Custom Tag
              </div>
            )}
            <div
              className="flex h-[40px] cursor-pointer flex-row px-[12px] font-poppins text-[14px] font-medium leading-[40px] text-[#011D58] hover:bg-[#F5F5F5]"
              onClick={handleRemove}
            >
              Remove
            </div>
          </div>
        </div>
      )}
      {showFollowType && (
        <PremiumFollowSolution
          isOpen={showFollowType}
          onClose={handleCloseShowFollowType}
          followSolution={handleFollowType}
          followType={followTypeName || ''}
          isMoving={true}
          sharedPortfolioId={sharedPortfolioId || undefined}
          solutionId={solutionId || ''}
        />
      )}
    </div>
  )
}
